import React from 'react';

import { Row, Col } from '../../../../grid';
import StatusIndicator from '../../status/status-indicator';
import { LoadingBlock } from '../../loading/progress';
import { AppSummaryInstanceProps, SummaryCell } from '../index';

import { useGetSummary } from '../../../../libs/summary';
import { IEpxHyperSettings } from '../../../../libs/apps/epxhyper';
import { TriageCategory } from '../../../../contexts/triage/types';

interface SummaryEntry {
  min?: number;
  max?: number;
  avg?: number;
  stddev?: number;
}

export interface EpxHyperSummaryResponse {
  summary: {
    systolic?: SummaryEntry;
    diastolic?: SummaryEntry;
  };
  settings: IEpxHyperSettings;
}

const StdDevStatus = <span>{'\u00B1 '}</span>;

const getStatus = (
  thresholds: IEpxHyperSettings['thresholds'],
  value: number,
  type: 'upper' | 'lower', // upper = systolic, lower = diastolic
  key: 'min' | 'max' | 'avg'
): JSX.Element => {
  let level = TriageCategory.Success;
  if (key === 'min') {
    if (value <= thresholds.red[type].min) {
      level = TriageCategory.Danger;
    }
  } else if (key === 'max') {
    if (value >= thresholds.red[type].max) {
      level = TriageCategory.Danger;
    }
  } else if (key === 'avg') {
    if (value >= thresholds.red[type].avg) {
      level = TriageCategory.Danger;
    } else if (value >= thresholds.yellow[type].avg) {
      level = TriageCategory.Warning;
    }
  }
  return (
    <StatusIndicator category={level} title={false} isAltCategoryWording />
  );
};

const EpxHyperSummary = ({ params }: AppSummaryInstanceProps): JSX.Element => {
  const { data, isLoading } = useGetSummary<EpxHyperSummaryResponse>(
    '/epxhyper/data_summary/',
    params
  );
  if (!data || isLoading) {
    return <LoadingBlock />;
  }
  const { summary, settings } = data;
  const { thresholds } = settings;
  return (
    <div>
      <h4 className="mb-8">Blood Pressure Summary from Past 2 Weeks</h4>
      <Row>
        <Col sm={6}>
          <SummaryCell
            label="Systolic Min."
            value={summary?.systolic?.min}
            status={getStatus(
              thresholds,
              summary?.systolic?.min,
              'upper',
              'min'
            )}
            unit="mmHg"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Systolic Max."
            value={summary?.systolic?.max}
            status={getStatus(
              thresholds,
              summary?.systolic?.max,
              'upper',
              'max'
            )}
            unit="mmHg"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Systolic Avg."
            value={summary?.systolic?.avg}
            status={getStatus(
              thresholds,
              summary?.systolic?.avg,
              'upper',
              'avg'
            )}
            unit="mmHg"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Systolic StdDev."
            value={summary?.systolic?.stddev}
            status={StdDevStatus}
            unit="mmHg"
          />
        </Col>
      </Row>
      <Row>
        <Col sm={6}>
          <SummaryCell
            label="Diastolic Min."
            value={summary?.diastolic?.min}
            status={getStatus(
              thresholds,
              summary?.diastolic?.min,
              'lower',
              'min'
            )}
            unit="mmHg"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Diastolic Max."
            value={summary?.diastolic?.max}
            status={getStatus(
              thresholds,
              summary?.diastolic?.max,
              'lower',
              'max'
            )}
            unit="mmHg"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Diastolic Avg."
            value={summary?.diastolic?.avg}
            status={getStatus(
              thresholds,
              summary?.diastolic?.avg,
              'lower',
              'avg'
            )}
            unit="mmHg"
          />
        </Col>
        <Col sm={6}>
          <SummaryCell
            label="Diastolic StdDev."
            value={summary?.diastolic?.stddev}
            status={StdDevStatus}
            unit="mmHg"
          />
        </Col>
      </Row>
    </div>
  );
};

export default EpxHyperSummary;
